import { useRef, useState } from 'react';
import { Bold, Italic, Link2, List, ListOrdered } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { insertLink, toggleEmphasis, toggleList } from '../lib/curatedTextToolbar';
import type { TextEdit } from '../lib/curatedTextToolbar';
import { CuratedText } from './CuratedText';
import { TextAreaField } from './Field';
import { Tabs } from './Tabs';

/**
 * A box for curated text, with a small toolbar and a preview — TROOP-256, for an Info page
 * section's body.
 *
 * **A plain textarea with buttons that type the syntax**, not a rich-text editor. One stored
 * format: pasting Markdown works, and somebody who has never heard of it clicks Bold. A WYSIWYG
 * editor was considered on the ticket and not chosen — it stores HTML or a JSON tree, which brings
 * the sanitizer this product deliberately does not have, and a second stored format is a second
 * renderer.
 *
 * **Preview is drawn by `CuratedText`, the one renderer** — so a link shows here with its host
 * beside it exactly as families will see it, and one the renderer will not draw shows as the
 * characters typed before the server says why on save.
 *
 * SPEC-GAP: the design system names no editor toolbar. Built from `Tabs` for Write/Preview, the
 * existing `Field` box, and icon buttons at `--tap-min` in `--color-text-secondary`.
 */
export function CuratedTextEditor({
  label,
  value,
  onChange,
  error,
  hint,
  rows = 8,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  rows?: number;
}) {
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const box = useRef<HTMLTextAreaElement>(null);

  /** Applies a toolbar edit and puts the selection back where the edit says, after React renders. */
  function apply(edit: (text: string, start: number, end: number) => TextEdit) {
    const el = box.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = edit(value, start, end);

    onChange(next.text);

    requestAnimationFrame(() => {
      if (!box.current) return;
      box.current.focus();
      box.current.setSelectionRange(next.selectionStart, next.selectionEnd);
    });
  }

  const write = (
    <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
      <div role="toolbar" aria-label="Formatting" className="flex flex-wrap" style={{ gap: 'var(--space-1)' }}>
        <ToolButton label="Bold" icon={Bold} onClick={() => apply((t, s, e) => toggleEmphasis(t, s, e, '**'))} />
        <ToolButton label="Italic" icon={Italic} onClick={() => apply((t, s, e) => toggleEmphasis(t, s, e, '*'))} />
        <ToolButton label="Bulleted list" icon={List} onClick={() => apply((t, s, e) => toggleList(t, s, e, 'bullet'))} />
        <ToolButton
          label="Numbered list"
          icon={ListOrdered}
          onClick={() => apply((t, s, e) => toggleList(t, s, e, 'number'))}
        />
        <ToolButton label="Link" icon={Link2} onClick={() => apply(insertLink)} />
      </div>

      <TextAreaField
        ref={box}
        label={label}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        error={error}
        hint={hint}
      />
    </div>
  );

  const preview = value.trim() ? (
    <CuratedText
      text={value}
      style={{
        color: 'var(--color-text-primary)',
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3) var(--space-4)',
      }}
    />
  ) : (
    <p className="t-body" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
      Nothing written yet.
    </p>
  );

  return (
    <Tabs
      label={`${label}: write or preview`}
      selectedId={tab}
      onSelect={(id) => setTab(id as 'write' | 'preview')}
      tabs={[
        { id: 'write', label: 'Write', panel: write },
        { id: 'preview', label: 'Preview', panel: preview },
      ]}
    />
  );
}

function ToolButton({ label, icon: Icon, onClick }: { label: string; icon: LucideIcon; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      // Keeps the textarea's selection: a mousedown on a button would otherwise take focus first.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="inline-flex items-center justify-center"
      style={{
        minHeight: 'var(--tap-min)',
        minWidth: 'var(--tap-min)',
        background: 'none',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--color-text-secondary)',
        cursor: 'pointer',
      }}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  );
}
